import { queryOptions } from '@tanstack/react-query'
import {
  getLeagueGroup,
  isNotFound,
  listClubEnrollments,
  listGroupEnrollments,
  listLeagueGroups,
  type TeamEnrollmentRead,
} from '@volee/api-client'

import { api } from '../services/api.ts'

// The queries of the campionati. The girone's calendar lives in `matches.ts` instead, beside
// its two sisters — a fixture list is a fixture list wherever it is asked from.

export const LEAGUES_KEY = ['leagues'] as const

export function leagueGroupKey(groupId: number) {
  return [...LEAGUES_KEY, 'group', groupId] as const
}

export function seasonGroupsKey() {
  return [...LEAGUES_KEY, 'season'] as const
}

export function clubEnrollmentsKey(clubId: number) {
  return [...LEAGUES_KEY, 'club', clubId] as const
}

export function groupEnrollmentsKey(groupId: number) {
  return [...LEAGUES_KEY, 'enrollments', groupId] as const
}

/** «This girone does not exist» as opposed to «something broke»: the first has no Retry that
 *  could ever succeed. `isMissingMatch`'s twin. */
export function isMissingGroup(error: unknown): boolean {
  return isNotFound(error)
}

export function leagueGroupQuery(groupId: number) {
  return queryOptions({
    queryKey: leagueGroupKey(groupId),
    queryFn: () => getLeagueGroup(api, groupId),
  })
}

export function groupEnrollmentsQuery(groupId: number) {
  return queryOptions({
    queryKey: groupEnrollmentsKey(groupId),
    queryFn: async () => (await listGroupEnrollments(api, groupId)).items,
  })
}

/** No year in the key: the client never sends one, and the server resolves the season the
 *  nested girone then names — so the envelope's echo has no reader. */
export function clubEnrollmentsQuery(clubId: number) {
  return queryOptions({
    queryKey: clubEnrollmentsKey(clubId),
    queryFn: async () => (await listClubEnrollments(api, clubId)).items,
  })
}

/** Every girone of the season, for a reader with no incarico: a genitore or a giocatore has
 *  no società to scope the list to, and the server resolves the season itself. */
export function seasonGroupsQuery() {
  return queryOptions({
    queryKey: seasonGroupsKey(),
    queryFn: async () => (await listLeagueGroups(api)).items,
  })
}

/** One squad's entries, out of its club's list: what `/teams`, a squad's page and the
 *  dashboard's cards each ask of `clubEnrollmentsQuery`, written once. */
export function entriesOf(
  teamId: number,
  entries: readonly TeamEnrollmentRead[],
): readonly TeamEnrollmentRead[] {
  return entries.filter((entry) => entry.team.id === teamId)
}
